import { useEffect, useRef, useState } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  type MeasurementPlane,
  type PlaneVec2,
  addExclusion,
  cancelExclusionDraw,
  popExclusionVertex,
  pushExclusionVertex,
} from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { frameToWorld, projectToFrame } from '../../../../utils/measurementPlane/frame.ts';
import { RENDER_LIFT_M } from '../../../../utils/measurementPlane/geometry.ts';
import { OverlayLine, OverlayPoint } from './drawPreview.tsx';

const ACCENT = '#ff3b3b'; // red — matches committed cut-out boundaries
const DRAG_PX = 5; // pointer travel above this = camera orbit, not a click
const CLOSE_PX = 14; // click within this many px of the first vertex closes the hole

/**
 * In-canvas tool for cutting an exclusion hole into an existing measurement surface.
 * Works exactly like the surface-draw tool: each click raycasts the model, the cursor
 * point tracks the mouse on the model, and the polygon closes by clicking the first
 * point (or Enter). Committed vertices are stored in the target surface's coordinate
 * space (world XY for horizontal, frame (u,v) for oriented).
 */
export function MeasurementExclusionEditor(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const draw = useAppSelector((s) => s.measurementPlanes.exclusionDraw);
  const plane = useAppSelector((s) =>
    s.measurementPlanes.planes.find((p) => p.id === s.measurementPlanes.exclusionDraw.planeId),
  );
  const { camera, gl, scene } = useThree();
  // Cursor in WORLD space (the raw raycast hit) so it sits where the mouse points on the model.
  const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

  const drawRef = useRef(draw);
  drawRef.current = draw;

  // Plane (u,v) coordinate → world point, for rendering committed vertices on the surface.
  const toWorldVec = (p: MeasurementPlane, c: PlaneVec2): THREE.Vector3 => {
    if (p.frame) {
      const w = frameToWorld(c, p.frame);
      return new THREE.Vector3(
        w[0] + p.frame.normal[0] * RENDER_LIFT_M,
        w[1] + p.frame.normal[1] * RENDER_LIFT_M,
        w[2] + p.frame.normal[2] * RENDER_LIFT_M,
      );
    }
    return new THREE.Vector3(c[0], c[1], p.height + RENDER_LIFT_M);
  };

  useEffect(() => {
    if (!draw.active || !plane) return;
    const dom = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    let downPos: { x: number; y: number } | null = null;

    const rect = () => dom.getBoundingClientRect();

    // Math plane of the target surface (fallback when a click misses the model).
    const surfacePlane = plane.frame
      ? new THREE.Plane().setFromNormalAndCoplanarPoint(
          new THREE.Vector3(plane.frame.normal[0], plane.frame.normal[1], plane.frame.normal[2]),
          new THREE.Vector3(plane.frame.origin[0], plane.frame.origin[1], plane.frame.origin[2]),
        )
      : new THREE.Plane(new THREE.Vector3(0, 0, 1), -plane.height);

    // Raw WORLD hit (model first, then the surface plane) — same raycast as surface drawing.
    const worldAt = (clientX: number, clientY: number): THREE.Vector3 | null => {
      const r = rect();
      ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const modelHit = raycaster
        .intersectObjects(scene.children, true)
        .find((h) => (h.object as THREE.Object3D)?.userData?.layerName != null);
      if (modelHit) return modelHit.point.clone();
      return raycaster.ray.intersectPlane(surfacePlane, hit) ? hit.clone() : null;
    };

    // World hit → target surface's (u,v)/world-XY coords, for the committed vertex.
    const coordAt = (clientX: number, clientY: number): PlaneVec2 | null => {
      const w = worldAt(clientX, clientY);
      if (!w) return null;
      return plane.frame ? projectToFrame([w.x, w.y, w.z], plane.frame) : [w.x, w.y];
    };

    const worldToPx = (c: PlaneVec2) => {
      const p = toWorldVec(plane, c).project(camera);
      const r = rect();
      return { x: ((p.x + 1) / 2) * r.width + r.left, y: ((1 - p.y) / 2) * r.height + r.top };
    };

    const onDown = (e: PointerEvent) => {
      if (e.button === 0) downPos = { x: e.clientX, y: e.clientY };
    };

    const onMove = (e: PointerEvent) => {
      const w = worldAt(e.clientX, e.clientY);
      if (w) setCursor(w);
    };

    const onUp = (e: PointerEvent) => {
      if (e.button !== 0 || !downPos) return;
      const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
      downPos = null;
      if (moved > DRAG_PX) return; // camera orbit, not a click

      const { verts, planeId } = drawRef.current;
      if (!planeId) return;

      // Close by clicking near the first vertex.
      if (verts.length >= 3) {
        const first = worldToPx(verts[0]);
        if (Math.hypot(e.clientX - first.x, e.clientY - first.y) < CLOSE_PX) {
          dispatch(addExclusion({ planeId, polygon: verts.map((v) => [v[0], v[1]]) }));
          return;
        }
      }

      const c = coordAt(e.clientX, e.clientY);
      if (c) dispatch(pushExclusionVertex(c));
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const { verts, planeId } = drawRef.current;
        if (planeId && verts.length >= 3) {
          dispatch(addExclusion({ planeId, polygon: verts.map((v) => [v[0], v[1]]) }));
        }
      } else if (e.key === 'Escape') {
        if (drawRef.current.verts.length > 0) dispatch(popExclusionVertex());
        else dispatch(cancelExclusionDraw());
      }
    };

    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointermove', onMove);
    dom.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      dom.removeEventListener('pointermove', onMove);
      dom.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
      setCursor(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draw.active, draw.planeId, camera, gl, scene, dispatch]);

  if (!draw.active || !plane) return null;

  const vertsV = draw.verts.map((v) => toWorldVec(plane, v));
  // Open rubber-band: committed vertices, then the cursor (raw model hit) — tracks the mouse.
  const linePts = cursor ? [...vertsV, cursor] : vertsV;

  return (
    <group>
      <OverlayLine points={linePts} color={ACCENT} width={2} />
      {vertsV.map((p, i) => (
        <OverlayPoint key={i} position={p} color={ACCENT} radius={0.15} />
      ))}
      {cursor && <OverlayPoint position={cursor} color={ACCENT} radius={0.13} />}
    </group>
  );
}
